// src/modules/shopping-list/services/shoppingListService.js

import authService from '../../auth/services/authService';
import { buildApiUrl } from '../../../config/api';

// Helper para manejar respuestas y redirigir en caso de 401
const handleResponse = async (response) => {
    if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Token expirado');
    }
    return response;
};

export const shoppingListService = {
    /**
     * Generar lista de la compra para usuarios logueados
     */
    async generateShoppingList(menu) {
        const response = await fetch(buildApiUrl('/api/user/generate-shopping-list'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify({ menu }),
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al generar la lista de la compra');
        }

        return await response.json();
    },

    /**
     * Obtener lista de compra de la próxima semana
     */
    async getNextWeekShoppingList() {
        const response = await fetch(buildApiUrl('/api/user/shopping-list/next-week'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener la lista de la próxima semana');
        }

        return await response.json();
    },

    /**
     * Obtener lista de compra de la semana actual
     */
    async getCurrentWeekShoppingList() {
        const response = await fetch(buildApiUrl('/api/user/shopping-list/current-week'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener la lista de la semana actual');
        }

        return await response.json();
    },

    /**
     * Generar lista de compra para fechas específicas
     */
    async generateShoppingListForDates(weekStartDate, weekEndDate) {
        const response = await fetch(buildApiUrl('/api/user/shopping-list/generate'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify({
                week_start_date: weekStartDate,
                week_end_date: weekEndDate
            }),
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al generar la lista de compra');
        }

        return await response.json();
    },

    /**
     * Obtener lista de compra por ID
     */
    async getShoppingList(id) {
        const response = await fetch(buildApiUrl(`/api/user/shopping-list/${id}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener la lista de compra');
        }

        return await response.json();
    },

    /**
     * Marcar/desmarcar item de la lista de compra
     */
    async toggleShoppingListItem(itemId) {
        const response = await fetch(buildApiUrl(`/api/user/shopping-list/item/${itemId}/toggle`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al cambiar el estado del item');
        }

        return await response.json();
    },

    /**
     * Agregar item manual a la lista de compra
     */
    async addManualItem(listId, name, quantity, category = null) {
        const response = await fetch(buildApiUrl(`/api/user/shopping-list/${listId}/items`), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify({ name, quantity, category }),
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al agregar el item');
        }

        return await response.json();
    },

    /**
     * Completar lista de compra
     */
    async completeShoppingList(listId) {
        const response = await fetch(buildApiUrl(`/api/user/shopping-list/${listId}/complete`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al completar la lista');
        }

        return await response.json();
    },

    /**
     * Descompletar lista de compra
     */
    async uncompleteShoppingList(listId) {
        const response = await fetch(buildApiUrl(`/api/user/shopping-list/${listId}/uncomplete`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al descompletar la lista');
        }

        return await response.json();
    },

    /**
     * Obtener todas las listas de compra
     */
    async getAllShoppingLists(isCompleted = null) {
        let url = buildApiUrl('/api/user/shopping-list');
        if (isCompleted !== null) {
            url += `?is_completed=${isCompleted}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener las listas de compra');
        }

        return await response.json();
    },

    /**
     * Sugerencias de categorías por prefijo
     */
    async getCategorySuggestions(query, limit = 10) {
        const trimmed = (query || '').trim();
        if (trimmed.length < 1) return [];

        const url = buildApiUrl(`/api/user/shopping-list/categories?q=${encodeURIComponent(trimmed)}&limit=${limit}`);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            return [];
        }

        const data = await response.json();
        return data.categories || [];
    },

    /**
     * Eliminar lista de compra
     */
    async deleteShoppingList(listId) {
        const response = await fetch(buildApiUrl(`/api/user/shopping-list/${listId}`), {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al eliminar la lista');
        }

        return await response.json();
    }
};
